import { useEffect } from 'react';

/**
 * Per-route document metadata for the SPA (SEO). The static head in index.html carries the full
 * brand meta ("Duck Duck Space" + description + OG) that crawlers see for every path of the shell;
 * this hook lets an individual PAGE override the title/description while it is mounted and — for
 * app-internal pages (auth, lobby, private rooms) — inject `noindex` so a rendered crawl never
 * indexes them. Everything is restored on unmount, so the landing keeps the canonical defaults.
 */
export interface PageMeta {
  /** Full document title for this page (e.g. "Lobby — Duck Duck Space"). */
  title?: string;
  /** Overrides <meta name="description"> while mounted. */
  description?: string;
  /** App-internal page: inject <meta name="robots" content="noindex, nofollow"> while mounted. */
  noindex?: boolean;
}

export function usePageMeta({ title, description, noindex }: PageMeta): void {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const descEl = document.head.querySelector('meta[name="description"]');
    const prevDesc = descEl?.getAttribute('content') ?? null;
    if (description) descEl?.setAttribute('content', description);

    let robotsEl: HTMLMetaElement | null = null;
    let canonicalEl: Element | null = null;
    if (noindex) {
      robotsEl = document.createElement('meta');
      robotsEl.name = 'robots';
      robotsEl.content = 'noindex, nofollow';
      document.head.appendChild(robotsEl);
      // noindex + a canonical pointing at another URL is a mixed signal Google warns can bleed the
      // noindex onto the canonical target (the landing) — drop the canonical while noindexed.
      canonicalEl = document.head.querySelector('link[rel="canonical"]');
      canonicalEl?.remove();
    }

    return () => {
      if (title) document.title = prevTitle;
      if (description && prevDesc !== null) descEl?.setAttribute('content', prevDesc);
      robotsEl?.remove();
      if (canonicalEl) document.head.appendChild(canonicalEl);
    };
  }, [title, description, noindex]);
}
